// src/stock/prets/PretsListe.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Card, Table, Row, Col, Form, Button, Spinner, Tooltip, OverlayTrigger, Pagination, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn"><i className="ri-close-line fs-5"></i></button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   PAGINATION
   ============================================================ */
const TablePagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <Pagination className="pagination-charte mb-0">
            <Pagination.Prev onClick={() => onPageChange(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                    <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => onPageChange(pageNum)}>
                        {pageNum}
                    </Pagination.Item>
                );
            })}
            <Pagination.Next onClick={() => onPageChange(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
    );
};

/* ============================================================
   STATUTS
   ============================================================ */
const STATUTS = {
    en_attente: { label: 'En attente', gradient: 'linear-gradient(135deg, #64748b, #475569)', icon: 'ri-time-line' },
    approuve:   { label: 'Approuvé',   gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', icon: 'ri-checkbox-circle-line' },
    en_cours:   { label: 'En cours',   gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: 'ri-play-line' },
    retourne:   { label: 'Retourné',   gradient: 'linear-gradient(135deg, #10b981, #059669)', icon: 'ri-checkbox-circle-line' },
    en_retard:  { label: 'En retard',  gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: 'ri-alarm-warning-line' },
    annule:     { label: 'Annulé',     gradient: 'linear-gradient(135deg, #94a3b8, #64748b)', icon: 'ri-close-line' }
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function PretsListe() {
    const navigate = useNavigate();
    const [prets, setPrets] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('date_pret');
    const [sortDirection, setSortDirection] = useState('desc');
    const [actionLoadingId, setActionLoadingId] = useState(null);

    // Toast
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    // Modal de suppression
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pretToDelete, setPretToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => { fetchPrets(); fetchStats(); }, [filtreStatut]);
    useEffect(() => { setCurrentPage(1); }, [filterText, dateDebut, dateFin, filtreStatut, itemsPerPage]);

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(hideToast, 5000);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchPrets = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");
            const params = new URLSearchParams();
            if (filtreStatut !== 'tous') params.append('statut', filtreStatut);
            const url = `${API_BASE_URL}/prets.php${params.toString() ? '?' + params.toString() : ''}`;
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (!res.ok) throw new Error(`Erreur HTTP: ${res.status}`);
            const result = await res.json();
            setPrets(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/prets.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            setStats(result.data);
        } catch (err) {
            console.error(err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const retournerPret = async (id_pret) => {
        setActionLoadingId(id_pret);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/prets.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_statut', id_pret, statut: 'retourne' })
            });
            const result = await res.json();
            if (result.success) {
                showToast('success', 'Retour enregistré', 'Le prêt a été clôturé avec succès.');
                await fetchPrets();
                await fetchStats();
            } else {
                showToast('error', 'Erreur', result.message);
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setActionLoadingId(null);
        }
    };

    const approuverPret = async (id_pret) => {
        setActionLoadingId(id_pret);
        try {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user") || '{}');
            const res = await fetch(`${API_BASE_URL}/prets.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_statut',
                    id_pret,
                    statut: 'approuve',
                    id_utilisateur_approbateur: user.id_utilisateur || user.id
                })
            });
            const result = await res.json();
            if (result.success) {
                showToast('success', 'Prêt approuvé', "L'article est maintenant en prêt.");
                await fetchPrets();
                await fetchStats();
            } else {
                showToast('error', 'Erreur', result.message);
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleDeleteClick = (pret) => {
        setPretToDelete(pret);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!pretToDelete) return;
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/prets.php?id=${pretToDelete.id_pret}&pret=1`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            if (result.success) {
                showToast('success', 'Prêt supprimé', `Le prêt ${pretToDelete.numero_pret} a été supprimé.`);
                setShowDeleteModal(false);
                setPretToDelete(null);
                await fetchPrets();
                await fetchStats();
            } else {
                showToast('error', 'Erreur', result.message);
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setDeleting(false);
        }
    };

    const resetFilters = () => {
        setFilterText('');
        setFiltreStatut('tous');
        setDateDebut('');
        setDateFin('');
        setCurrentPage(1);
    };

    /* ============================================================
       FILTRAGE / TRI / PAGINATION
       ============================================================ */
    const filteredAndSorted = useMemo(() => {
        const search = filterText.trim().toLowerCase();
        let filtered = prets.filter(p => {
            const matchText = !search ||
                (p.numero_pret || '').toLowerCase().includes(search) ||
                (p.article_designation || '').toLowerCase().includes(search) ||
                (p.article_reference || '').toLowerCase().includes(search) ||
                (p.emprunteur_nom || '').toLowerCase().includes(search) ||
                (p.emprunteur_prenom || '').toLowerCase().includes(search);

            let dateMatch = true;
            const pDate = p.date_pret?.split('T')[0] || '';
            if (dateDebut && dateFin) dateMatch = pDate >= dateDebut && pDate <= dateFin;
            else if (dateDebut) dateMatch = pDate >= dateDebut;
            else if (dateFin) dateMatch = pDate <= dateFin;

            return matchText && dateMatch;
        });

        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            if (sortField === 'date_pret' || sortField === 'date_retour_prevue') {
                aVal = new Date(aVal || 0);
                bVal = new Date(bVal || 0);
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }
            return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });

        return filtered;
    }, [prets, filterText, dateDebut, dateFin, sortField, sortDirection]);

    const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    /* ============================================================
       STATS DYNAMIQUES
       ============================================================ */
    const dynamicStats = useMemo(() => {
        const total = filteredAndSorted.length;
        const enAttente = filteredAndSorted.filter(p => p.statut === 'en_attente').length;
        const enCours = filteredAndSorted.filter(p => p.statut === 'en_cours' || p.statut === 'approuve').length;
        const retournes = filteredAndSorted.filter(p => p.statut === 'retourne').length;
        const montantTotal = filteredAndSorted
            .filter(p => p.statut === 'en_cours' || p.statut === 'approuve')
            .reduce((sum, p) => sum + (parseFloat(p.montant) || 0), 0);
        return { total, enAttente, enCours, retournes, montantTotal };
    }, [filteredAndSorted]);

    /* ============================================================
       HELPERS
       ============================================================ */
    const SortableHeader = ({ field, children, className = '' }) => (
        <th className={`sortable-th-charte ${className}`} onClick={() => handleSort(field)}>
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortField === field && (
                    <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const getStatutBadge = (statut) => {
        const cfg = STATUTS[statut] || STATUTS.en_attente;
        return (
            <span className="status-badge-charte" style={{ background: cfg.gradient }}>
                <i className={cfg.icon}></i> {cfg.label}
            </span>
        );
    };

    const isEnRetard = (p) => p.statut === 'en_cours' && new Date(p.date_retour_prevue) < new Date();

    /* ============================================================
       LOADING
       ============================================================ */
   
      if (loading && !prets.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des données...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }
   
 

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <ToastNotification show={toast.show} onClose={hideToast} type={toast.type} title={toast.title} message={toast.message} />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper"><i className="ri-hand-coin-line"></i></div>
                            <div>
                                <h5 className="header-title">Gestion des prêts</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {dynamicStats.total}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-time-line"></i> En attente {dynamicStats.enAttente}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-play-line"></i> En cours {dynamicStats.enCours}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-check-line"></i> Retournés {dynamicStats.retournes}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchPrets} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => navigate('/stock/pretsAjouter')}>
                                <i className="ri-add-line"></i> Nouveau prêt
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI (compactes) ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-compact kpi-primary">
                                    <div className="kpi-icon-compact"><i className="ri-hand-coin-line"></i></div>
                                    <div>
                                        <div className="kpi-label-compact">Prêts en cours</div>
                                        <div className="kpi-value-compact">{stats.en_cours || 0}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-compact kpi-danger">
                                    <div className="kpi-icon-compact"><i className="ri-alarm-warning-line"></i></div>
                                    <div>
                                        <div className="kpi-label-compact">En retard</div>
                                        <div className="kpi-value-compact">{stats.en_retard || 0}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-compact kpi-success">
                                    <div className="kpi-icon-compact"><i className="ri-checkbox-circle-line"></i></div>
                                    <div>
                                        <div className="kpi-label-compact">Retournés</div>
                                        <div className="kpi-value-compact">{stats.retournes || 0}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-compact kpi-gold">
                                    <div className="kpi-icon-compact"><i className="ri-money-dollar-circle-line"></i></div>
                                    <div>
                                        <div className="kpi-label-compact">Montant en cours</div>
                                        <div className="kpi-value-compact">
                                            {Number(stats.montant_en_cours || 0).toLocaleString('fr-FR')} <small>FCFA</small>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="search-box-charte">
                                        <i className="ri-search-line search-icon-charte"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher n° prêt, article, emprunteur..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="search-input-charte"
                                        />
                                    </div>
                                </Col>
                                <Col md="2">
                                    <Form.Control type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="filter-select-charte" />
                                </Col>
                                <Col md="2">
                                    <Form.Control type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="filter-select-charte" />
                                </Col>
                                <Col md="2">
                                    <Form.Select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)} className="filter-select-charte">
                                        <option value="tous">Tous statuts</option>
                                        <option value="en_attente">En attente</option>
                                        <option value="approuve">Approuvés</option>
                                        <option value="en_cours">En cours</option>
                                        <option value="retourne">Retournés</option>
                                        <option value="en_retard">En retard</option>
                                        <option value="annule">Annulés</option>
                                    </Form.Select>
                                </Col>
                                <Col md="1">
                                    <Form.Select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="filter-select-charte">
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </Form.Select>
                                </Col>
                                <Col md="1" className="text-end">
                                    {(filterText || dateDebut || dateFin || filtreStatut !== 'tous') ? (
                                        <Button onClick={resetFilters} className="btn-modern-light-outline">
                                            <i className="ri-close-line"></i>
                                        </Button>
                                    ) : (
                                        <span className="count-badge-charte">
                                            <i className="ri-stack-line"></i> {filteredAndSorted.length}
                                        </span>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-hand-coin-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des prêts</span>
                                    <span className="badge-header-light">{filteredAndSorted.length}</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                             <SortableHeader field="article_designation">Article</SortableHeader>
                                            <SortableHeader field="emprunteur_nom">Emprunteur</SortableHeader>
                                            <SortableHeader field="date_pret">Date prêt</SortableHeader>
                                            <SortableHeader field="date_retour_prevue">Retour prévu</SortableHeader>
                                            <SortableHeader field="statut" className="text-center">Statut</SortableHeader>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((p) => {
                                            const isUpdating = actionLoadingId === p.id_pret;
                                            return (
                                                <tr key={p.id_pret} className={isEnRetard(p) ? 'row-late-charte' : ''}>
                                                    
                                                  
                                                    {/* Article + montant */}
                                                    <td style={{ minWidth: '220px' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="article-icon-charte">
                                                                <i className="ri-hand-coin-line"></i>
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                    {p.article_designation}
                                                                </div>
                                                                <small className="text-muted d-block">
                                                                    {p.article_reference}
                                                                </small>
                                                                {Number(p.montant) > 0 && (
                                                                    <small className="fw-semibold" style={{ color: COLORS.success, fontSize: '0.75rem' }}>
                                                                        <i className="ri-money-dollar-circle-line me-1"></i>
                                                                        {Number(p.montant).toLocaleString('fr-FR')} FCFA
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Emprunteur */}
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="emprunteur-avatar-charte">
                                                                {(p.emprunteur_prenom || '?').charAt(0)}{(p.emprunteur_nom || '?').charAt(0)}
                                                            </div>
                                                            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                {p.emprunteur_prenom} {p.emprunteur_nom}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Date prêt */}
                                                    <td>
                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                            {formatDate(p.date_pret)}
                                                        </div>
                                                    </td>

                                                    {/* Retour prévu */}
                                                    <td>
                                                        <div className={isEnRetard(p) ? 'text-danger fw-semibold' : 'fw-semibold'} style={{ fontSize: '0.85rem' }}>
                                                            {formatDate(p.date_retour_prevue)}
                                                        </div>
                                                        {isEnRetard(p) && (
                                                            <span className="badge-late-charte">
                                                                <i className="ri-alarm-warning-line"></i> Retard
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* Statut */}
                                                    <td className="text-center">{getStatutBadge(p.statut)}</td>

                                                    {/* Actions */}
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Modifier le prêt</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => navigate(`/stock/pretsModifier/${p.id_pret}`)}
                                                                    style={{ '--btn-color': COLORS.info }}
                                                                >
                                                                    <i className="ri-edit-line"></i>
                                                                </button>
                                                            </OverlayTrigger>

                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer le prêt</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => handleDeleteClick(p)}
                                                                    style={{ '--btn-color': COLORS.danger }}
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </button>
                                                            </OverlayTrigger>

                                                            {p.statut === 'en_attente' && (
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Approuver le prêt</Tooltip>}>
                                                                    <button
                                                                        className="action-btn-charte"
                                                                        onClick={() => approuverPret(p.id_pret)}
                                                                        disabled={isUpdating}
                                                                        style={{ '--btn-color': COLORS.success }}
                                                                    >
                                                                        {isUpdating ? (
                                                                            <Spinner size="sm" animation="border" style={{ width: '14px', height: '14px' }} />
                                                                        ) : (
                                                                            <i className="ri-check-line"></i>
                                                                        )}
                                                                    </button>
                                                                </OverlayTrigger>
                                                            )}

                                                            {(p.statut === 'en_cours' || p.statut === 'approuve') && (
                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Marquer comme retourné</Tooltip>}>
                                                                    <button
                                                                        className="action-btn-charte"
                                                                        onClick={() => retournerPret(p.id_pret)}
                                                                        disabled={isUpdating}
                                                                        style={{ '--btn-color': COLORS.primary }}
                                                                    >
                                                                        {isUpdating ? (
                                                                            <Spinner size="sm" animation="border" style={{ width: '14px', height: '14px' }} />
                                                                        ) : (
                                                                            <i className="ri-arrow-go-back-line"></i>
                                                                        )}
                                                                    </button>
                                                                </OverlayTrigger>
                                                            )}

                                                            {p.statut === 'annule' && (
                                                                <span className="text-muted small">
                                                                    <i className="ri-close-circle-fill me-1" style={{ color: COLORS.danger }}></i>
                                                                    Annulé
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-hand-coin-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">
                                                            {filterText || dateDebut || dateFin || filtreStatut !== 'tous'
                                                                ? 'Aucun résultat'
                                                                : 'Aucun prêt'}
                                                        </h6>
                                                        <p className="text-muted small mb-3">
                                                            {filterText || dateDebut || dateFin || filtreStatut !== 'tous'
                                                                ? "Essayez de modifier vos critères de recherche."
                                                                : "Commencez par créer votre premier prêt."}
                                                        </p>
                                                        {!(filterText || dateDebut || dateFin || filtreStatut !== 'tous') && (
                                                            <Button className="btn-modern-primary" onClick={() => navigate('/stock/pretsAjouter')}>
                                                                <i className="ri-add-line me-2"></i>Créer un prêt
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSorted.length)}</strong> sur <strong>{filteredAndSorted.length}</strong> prêt(s)
                            </div>
                            <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== MODAL DE SUPPRESSION ===== */}
            <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered>
                <Modal.Header closeButton={!deleting} style={{ background: COLORS.danger, color: 'white', borderTopLeftRadius: '0px', borderTopRightRadius: '0px' }}>
                    <Modal.Title style={{ color: 'white', fontSize: '1.1rem' }}>
                        <i className="ri-delete-bin-line me-2"></i>
                        Confirmer la suppression
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {pretToDelete && (
                        <>
                            <p className="mb-3">Êtes-vous sûr de vouloir supprimer ce prêt ? Cette action est irréversible.</p>
                            <div className="p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">N° Prêt</span>
                                    <strong>{pretToDelete.numero_pret}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Article</span>
                                    <strong>{pretToDelete.article_designation}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Emprunteur</span>
                                    <strong>{pretToDelete.emprunteur_prenom} {pretToDelete.emprunteur_nom}</strong>
                                </div>
                                {Number(pretToDelete.montant) > 0 && (
                                    <div className="d-flex justify-content-between">
                                        <span className="text-muted small">Montant</span>
                                        <strong style={{ color: COLORS.success }}>
                                            {Number(pretToDelete.montant).toLocaleString('fr-FR')} FCFA
                                        </strong>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ border: 'none', padding: '1rem 1.5rem' }}>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={deleting}
                        style={{ borderRadius: '50px' }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        disabled={deleting}
                        style={{ background: COLORS.danger, border: 'none', borderRadius: '50px', padding: '0.5rem 1.5rem' }}
                    >
                        {deleting ? (
                            <><Spinner size="sm" className="me-2" />Suppression...</>
                        ) : (
                            <><i className="ri-delete-bin-line me-2"></i>Supprimer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                    --gold: #F4C430;
                }

                .header-modern { background: white; padding: 1.2rem 1.8rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid var(--secondary); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, var(--primary), var(--secondary)); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25); }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern { background: #f1f4f9; color: #475569; padding: 0.3rem 0.8rem; border-radius: 50px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== KPI COMPACTES ===== */
                .kpi-compact {
                    background: white;
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 0.9rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                    min-height: 80px;
                }
                .kpi-compact:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-gold    { border-left-color: var(--gold); }

                .kpi-icon-compact {
                    width: 42px;
                    height: 42px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-compact { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-compact { background: ${COLORS.success}15; color: var(--success); }
                .kpi-warning .kpi-icon-compact { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-danger  .kpi-icon-compact { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-gold    .kpi-icon-compact { background: rgba(244, 196, 48, 0.15); color: var(--gold); }

                .kpi-label-compact {
                    font-size: 0.7rem;
                    color: #94a3b8;
                    text-transform: uppercase;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    margin-bottom: 2px;
                }
                .kpi-value-compact {
                    font-size: 1.3rem;
                    font-weight: 700;
                    color: #1e293b;
                    line-height: 1.2;
                }
                .kpi-value-compact small {
                    font-size: 0.7rem;
                    color: #94a3b8;
                    font-weight: 500;
                }

                /* ===== FILTRES ===== */
                .filter-card-charte { background: linear-gradient(135deg, var(--primary-dark), var(--primary)); border-radius: 12px; border: none; box-shadow: 0 8px 24px rgba(41,90,167,0.15); }
                .search-box-charte { position: relative; }
                .search-icon-charte { position: absolute; top: 50%; left: 1rem; transform: translateY(-50%); color: var(--primary); font-size: 1rem; z-index: 2; pointer-events: none; }
                .search-input-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem 0.6rem 2.75rem !important; font-size: 0.9rem; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .search-input-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .filter-select-charte { background: white !important; border: none !important; border-radius: 50px !important; padding: 0.6rem 1rem !important; font-size: 0.85rem; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }
                .btn-modern-light-outline { background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.3); color: white; border-radius: 50px; padding: 0.5rem 1rem; font-size: 0.85rem; font-weight: 500; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-light-outline:hover { background: rgba(255,255,255,0.25); color: white; }
                .count-badge-charte { background: rgba(255,255,255,0.95); color: var(--primary); padding: 0.6rem 1.25rem; border-radius: 50px; font-size: 0.85rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }

                /* ===== CARD HEADER ===== */
                .card-header-gradient { background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%); border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; padding: 1rem 1.5rem; border: none; }
                .badge-header-light { background: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.3); border-radius: 50px; padding: 0.25rem 0.75rem; font-size: 0.75rem; font-weight: 600; }

                /* ===== TABLE ===== */
                .table-charte thead th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 0.9rem 0.75rem; }
                .table-charte tbody td { padding: 0.9rem 0.75rem; vertical-align: middle; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .sortable-th-charte { cursor: pointer; user-select: none; transition: color 0.2s; }
                .sortable-th-charte:hover { color: var(--primary); }
                .row-late-charte { background: #fef2f2 !important; }
                .row-late-charte:hover { background: #fee2e2 !important; }

                .article-icon-charte { width: 38px; height: 38px; border-radius: 10px; background: ${COLORS.primary}15; color: var(--primary); display: flex; align-items: center; justify-content: center; font-size: 1rem; flex-shrink: 0; }
                .emprunteur-avatar-charte { width: 32px; height: 32px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), var(--primary-dark)); color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.7rem; flex-shrink: 0; box-shadow: 0 2px 6px rgba(41,90,167,0.2); }
                .reference-badge-charte { display: inline-flex; align-items: center; background: #f1f5f9; padding: 0.25rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-family: monospace; color: #475569; }
                .status-badge-charte { display: inline-flex; align-items: center; gap: 4px; color: white; padding: 0.3rem 0.75rem; border-radius: 50px; font-size: 0.7rem; font-weight: 600; white-space: nowrap; }
                .badge-late-charte { display: inline-flex; align-items: center; gap: 3px; background: var(--danger); color: white; padding: 0.15rem 0.5rem; border-radius: 50px; font-size: 0.65rem; font-weight: 600; margin-left: 0.4rem; }

                .action-btn-charte { width: 36px; height: 36px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: white; border: 1.5px solid #e2e8f0; color: #64748b; font-size: 1rem; transition: all 0.2s ease; cursor: pointer; }
                .action-btn-charte:hover:not(:disabled) { border-color: var(--btn-color, ${COLORS.primary}); color: var(--btn-color, ${COLORS.primary}); transform: translateY(-1px); }
                .action-btn-charte:disabled { opacity: 0.5; cursor: not-allowed; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte { width: 72px; height: 72px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8; }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; background: white; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); flex-wrap: wrap; gap: 1rem; }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link { border-radius: 10px; border: 1.5px solid #e2e8f0; color: #1e293b; padding: 0.5rem 0.9rem; font-weight: 500; font-size: 0.85rem; transition: all 0.2s; }
                .pagination-charte .page-item.active .page-link { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border-color: var(--primary); color: white; box-shadow: 0 4px 12px rgba(41,90,167,0.3); }
                .pagination-charte .page-item .page-link:hover { background: #f1f5f9; border-color: var(--secondary); color: var(--primary); transform: translateY(-1px); }

                /* ===== BOUTONS ===== */
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; font-size: 0.85rem; font-weight: 500; color: #4b5563; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-outline:hover { border-color: var(--secondary); color: var(--secondary); background: #f0fbff; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(33,172,205,0.15); }
                .btn-modern-primary { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; font-size: 0.85rem; transition: all 0.2s ease; box-shadow: 0 4px 12px rgba(41,90,167,0.3); display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white; }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; opacity: 0.8; }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }

                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
